/* Kiti: Expected delivery date (UK)
   Rules:
   - Cut-off: 15:30 UK time (Europe/London)
   - Dispatch days: Monday–Thursday (excludes UK bank holidays)
   - Delivery days: Tuesday–Friday (excludes UK bank holidays)
   - Orders after cut-off dispatch next dispatch day
   - Orders on Fri/Sat/Sun dispatch next dispatch day (usually Monday unless bank holiday)

   Markup contract:
   - Wrapper: [data-kiti-delivery-estimate]
   - Value element: [data-kiti-delivery-estimate-value]
   - Optional attributes on wrapper:
     - data-cutoff-hour
     - data-cutoff-minute
*/

(function () {
  // Singleton module so variant re-renders don't duplicate listeners/logic
  var KED = window.KitiExpectedDelivery || {};
  if (KED.__inited) {
    if (typeof KED.scheduleUpdate === 'function') KED.scheduleUpdate();
    window.KitiExpectedDelivery = KED;
    return;
  }
  KED.__inited = true;

  var TZ = 'Europe/London';
  var GOV_UK_BANK_HOLIDAYS_URL = 'https://www.gov.uk/bank-holidays.json';
  var BANK_HOL_CACHE_KEY = 'kiti_bank_holidays_eaw_v1';
  var BANK_HOL_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

  // Cache Intl formatters (expensive to construct)
  var fmtNowParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  var fmtYmdParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  var fmtShort = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    day: 'numeric',
    month: 'long'
  });
  var fmtWeekday = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    weekday: 'long'
  });

  function partsToMap(parts) {
    var map = {};
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type !== 'literal') map[parts[i].type] = parts[i].value;
    }
    return map;
  }

  function londonNowParts() {
    var m = partsToMap(fmtNowParts.formatToParts(new Date()));
    return {
      year: parseInt(m.year, 10),
      month: parseInt(m.month, 10),
      day: parseInt(m.day, 10),
      hour: parseInt(m.hour, 10),
      minute: parseInt(m.minute, 10)
    };
  }

  function ymdInLondon(dUtc) {
    var m = partsToMap(fmtYmdParts.formatToParts(dUtc));
    return m.year + '-' + m.month + '-' + m.day;
  }

  function ymdFromUtcDate(dUtc) {
    var y = dUtc.getUTCFullYear();
    var m = String(dUtc.getUTCMonth() + 1).padStart(2, '0');
    var d = String(dUtc.getUTCDate()).padStart(2, '0');
    return y + '-' + m + '-' + d;
  }

  function utcDateFromYMD(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d));
  }

  function addDaysUtc(dUtc, days) {
    var d = new Date(dUtc.getTime());
    d.setUTCDate(d.getUTCDate() + days);
    return d;
  }

  function getCutoffFromEl(el) {
    var h = parseInt(el.getAttribute('data-cutoff-hour'), 10);
    var m = parseInt(el.getAttribute('data-cutoff-minute'), 10);
    return {
      hour: Number.isFinite(h) ? h : 15,
      minute: Number.isFinite(m) ? m : 30
    };
  }

  function isDispatchDay(dUtc, bankHolidaySet) {
    // Mon–Thu only, excluding bank holidays
    var dow = dUtc.getUTCDay(); // 0 Sun .. 6 Sat
    if (dow < 1 || dow > 4) return false;
    return !bankHolidaySet.has(ymdFromUtcDate(dUtc));
  }

  function isDeliveryDay(dUtc, bankHolidaySet) {
    // Tue–Fri only, excluding bank holidays
    var dow = dUtc.getUTCDay();
    if (dow < 2 || dow > 5) return false;
    return !bankHolidaySet.has(ymdFromUtcDate(dUtc));
  }

  function nextValidDay(fromUtc, bankHolidaySet, predicate) {
    var d = new Date(fromUtc.getTime());
    while (!predicate(d, bankHolidaySet)) d = addDaysUtc(d, 1);
    return d;
  }

  function formatDeliveryDateShort(dUtc) {
    // e.g. "13 January"
    return fmtShort.format(dUtc);
  }

  function formatDeliveryDateFull(dUtc) {
    // e.g. "Tuesday, 20 January"
    return fmtWeekday.format(dUtc) + ', ' + formatDeliveryDateShort(dUtc);
  }

  // One shared promise for bank holiday data (prevents re-fetching on variant re-renders)
  KED._bankHolidayPromise = KED._bankHolidayPromise || (function () {
    // Try localStorage cache first
    try {
      var cached = localStorage.getItem(BANK_HOL_CACHE_KEY);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.ts && Array.isArray(parsed.dates)) {
          if ((Date.now() - parsed.ts) < BANK_HOL_CACHE_TTL_MS) {
            return Promise.resolve(new Set(parsed.dates));
          }
        }
      }
    } catch (e) {}

    // Fetch from GOV.UK (fail open)
    return fetch(GOV_UK_BANK_HOLIDAYS_URL, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('Bank holiday fetch failed');
        return res.json();
      })
      .then(function (json) {
        var events = (json['england-and-wales'] && json['england-and-wales'].events) ? json['england-and-wales'].events : [];
        var dates = events.map(function (e) { return e.date; }).filter(Boolean);

        try {
          localStorage.setItem(BANK_HOL_CACHE_KEY, JSON.stringify({ ts: Date.now(), dates: dates }));
        } catch (e) {}

        return new Set(dates);
      })
      .catch(function () {
        return new Set();
      });
  })();

  // Memoise computed string briefly (variant changes often happen within seconds)
  KED._memo = KED._memo || { key: null, ts: 0, text: null };

  function memoKey(now, cutoff) {
    var after = (now.hour > cutoff.hour) || (now.hour === cutoff.hour && now.minute > cutoff.minute);
    return now.year + '-' + now.month + '-' + now.day + '|' + (after ? '1' : '0');
  }

  function computeDeliveryTextForEl(el) {
    var cutoff = getCutoffFromEl(el);
    var now = londonNowParts();
    var key = memoKey(now, cutoff);

    // 60s cache window
    if (KED._memo.key === key && (Date.now() - KED._memo.ts) < 60000 && KED._memo.text) {
      return Promise.resolve(KED._memo.text);
    }

    var orderDateUtc = utcDateFromYMD(now.year, now.month, now.day);
    var isAfterCutoff = (now.hour > cutoff.hour) || (now.hour === cutoff.hour && now.minute > cutoff.minute);

    return KED._bankHolidayPromise.then(function (bankHolidaySet) {
      // Dispatch day
      var dispatchUtc;
      if (isDispatchDay(orderDateUtc, bankHolidaySet) && !isAfterCutoff) {
        dispatchUtc = orderDateUtc;
      } else {
        dispatchUtc = nextValidDay(addDaysUtc(orderDateUtc, 1), bankHolidaySet, isDispatchDay);
      }

      // Delivery day
      var deliveryUtc = nextValidDay(addDaysUtc(dispatchUtc, 1), bankHolidaySet, isDeliveryDay);

      var tomorrowUtc = addDaysUtc(orderDateUtc, 1);
      var text;

      if (ymdInLondon(deliveryUtc) === ymdInLondon(tomorrowUtc)) {
        text = 'Tomorrow, ' + formatDeliveryDateShort(deliveryUtc);
      } else {
        text = formatDeliveryDateFull(deliveryUtc);
      }

      KED._memo.key = key;
      KED._memo.ts = Date.now();
      KED._memo.text = text;

      return text;
    });
  }

  function findEls() {
    return document.querySelectorAll('[data-kiti-delivery-estimate]');
  }

  // Avoid overlapping async updates when multiple variant-change triggers fire
  KED._updating = false;
  KED._pending = false;

  KED.updateAll = function () {
    var els = findEls();
    if (!els.length) return;

    // If an update is already running, queue one more
    if (KED._updating) {
      KED._pending = true;
      return;
    }

    KED._updating = true;

    for (var i = 0; i < els.length; i++) {
      var v = els[i].querySelector('[data-kiti-delivery-estimate-value]');
      if (v) v.textContent = 'Calculating…';
    }

    computeDeliveryTextForEl(els[0])
      .then(function (text) {
        for (var i = 0; i < els.length; i++) {
          var v = els[i].querySelector('[data-kiti-delivery-estimate-value]');
          if (v) v.textContent = text;
        }
      })
      .catch(function (err) {
        // Fail gracefully: don't leave the UI stuck on "Calculating…"
        console.warn('[Kiti delivery estimate] Failed to compute delivery estimate:', err);
        for (var i = 0; i < els.length; i++) {
          var v = els[i].querySelector('[data-kiti-delivery-estimate-value]');
          if (v) v.textContent = 'Next available delivery shown at checkout';
        }
      })
      .then(function () {
        KED._updating = false;
        if (KED._pending) {
          KED._pending = false;
          KED.updateAll();
        }
      });
  };

  // Debounced scheduling to cope with DOM swaps
  KED._rafId = null;
  KED._tId = null;
  KED.scheduleUpdate = function () {
    if (KED._rafId) cancelAnimationFrame(KED._rafId);
    if (KED._tId) clearTimeout(KED._tId);

    KED.updateAll();
    KED._rafId = requestAnimationFrame(function () { KED.updateAll(); });
    KED._tId = setTimeout(function () { KED.updateAll(); }, 50);
  };

  // Expose
  window.KitiExpectedDelivery = KED;

  // Initial (handle both defer + any edge cases)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      KED.scheduleUpdate();
    });
  } else {
    KED.scheduleUpdate();
  }

  // Variant change (Alchemy / Clean Canvas)
  // (listener lives once on document; variant HTML swaps won't remove it)
  if (!KED._listenerAdded) {
    KED._listenerAdded = true;
    document.addEventListener('on:variant:change', function () {
      KED.scheduleUpdate();
    });
  }
})();